import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

// Konfigurasi sidebar global (disimpan di localStorage)
// - hoverExpand: sidebar otomatis maximize saat di-hover (jika collapsed)
// - clickToggle: tombol toggle (panah) untuk minimize/maximize tetap aktif
// - mobileHide: di breakpoint mobile (<md) sidebar disembunyikan total (akses via hamburger)

const STORAGE_KEY = "sidebarConfig";

const DEFAULT_CONFIG = {
  hoverExpand: true,
  clickToggle: true,
  mobileHide: true,
};

function readConfig() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_CONFIG;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_CONFIG, ...parsed };
  } catch {
    return DEFAULT_CONFIG;
  }
}

const SidebarConfigContext = createContext({
  config: DEFAULT_CONFIG,
  updateConfig: () => {},
  resetConfig: () => {},
  // mobile drawer state
  mobileOpen: false,
  setMobileOpen: () => {},
  toggleMobile: () => {},
});

export function SidebarConfigProvider({ children }) {
  const [config, setConfig] = useState(readConfig);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch {
      // ignore quota errors
    }
    // Toggle attribute pada <html> agar CSS responsive bisa nge-override margin layout
    if (typeof document !== "undefined") {
      document.documentElement.dataset.sidebarMobileHide = config.mobileHide
        ? "true"
        : "false";
    }
  }, [config]);

  // Sync config across tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        setConfig(readConfig());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const updateConfig = useCallback((patch) => {
    setConfig((prev) => ({ ...prev, ...patch }));
  }, []);

  const resetConfig = useCallback(() => setConfig(DEFAULT_CONFIG), []);
  const toggleMobile = useCallback(() => setMobileOpen((v) => !v), []);

  return (
    <SidebarConfigContext.Provider
      value={{
        config,
        updateConfig,
        resetConfig,
        mobileOpen,
        setMobileOpen,
        toggleMobile,
      }}>
      {children}
    </SidebarConfigContext.Provider>
  );
}

export function useSidebarConfig() {
  return useContext(SidebarConfigContext);
}

export { DEFAULT_CONFIG as DEFAULT_SIDEBAR_CONFIG };
